/** Composer + keyboard: rAF sync (visualViewport + Telegram viewportHeight). */
import { getTg } from '../core/telegram.js';
import { isElement } from '../core/dom.js';
import {
  getOwnerChatAutoScrollEnabled,
  getOwnerChatSuppressKeyboardScroll
} from '../features/owner-chat/ui-state.js';
import {
  scrollOwnerChatToBottom,
  scrollOwnerChatToBottomSmooth
} from '../features/owner-chat/scroll.js';

let keyboardInsetPrev = 0;
let keyboardTrackRaf: number | null = null;
let keyboardTrackUntil = 0;

function computeChatKeyboardInset(): number {
  let inset = 0;
  try {
    const vv = window.visualViewport;
    if (vv) {
      inset = Math.max(0, window.innerHeight - vv.height - (vv.offsetTop || 0));
    }
  } catch (_) {}
  try {
    const t = getTg();
    if (t && typeof t.viewportHeight === 'number' && t.viewportHeight > 0) {
      const tgInset = Math.max(0, window.innerHeight - t.viewportHeight);
      if (tgInset > inset) inset = tgInset;
    }
  } catch (_) {}
  return inset;
}

function updateChatComposerHeight(): void {
  try {
    const active = document.querySelector('.view-active');
    if (!active) return;
    const composer =
      active.id === 'view-consult'
        ? document.getElementById('owner-chat-composer')
        : active.id === 'view-ai-assistant'
          ? document.getElementById('ai-chat-composer')
          : null;
    if (!composer) return;
    const h = Math.max(56, Math.round(composer.getBoundingClientRect().height || composer.offsetHeight || 0));
    document.documentElement.style.setProperty('--chat-composer-height', h + 'px');
  } catch (_) {}
}

function syncChatKeyboardInset(): void {
  const root = document.documentElement;
  const app = document.querySelector('.app');
  if (!app || !app.classList.contains('app--chat-open')) {
    if (keyboardInsetPrev !== 0) {
      root.style.setProperty('--keyboard-inset', '0px');
      app && app.classList.remove('app--keyboard-tracking');
    }
    keyboardInsetPrev = 0;
    return;
  }

  const inset = computeChatKeyboardInset();
  root.style.setProperty('--keyboard-inset', inset + 'px');
  updateChatComposerHeight();

  const insetChanged = Math.abs(inset - keyboardInsetPrev) >= 0.5;
  if (inset > 0 || insetChanged) {
    app.classList.add('app--keyboard-tracking');
  } else if (Date.now() > keyboardTrackUntil) {
    app.classList.remove('app--keyboard-tracking');
  }

  if (getOwnerChatSuppressKeyboardScroll()) {
    keyboardInsetPrev = inset;
    return;
  }
  if (getOwnerChatAutoScrollEnabled() && inset > 0 && insetChanged) {
    if (keyboardInsetPrev === 0) {
      scrollOwnerChatToBottomSmooth(420);
    } else if (inset > keyboardInsetPrev) {
      scrollOwnerChatToBottom();
    }
  }
  keyboardInsetPrev = inset;
}

function chatKeyboardTrackFrame(): void {
  keyboardTrackRaf = null;
  syncChatKeyboardInset();
  if (Date.now() < keyboardTrackUntil) {
    keyboardTrackRaf = requestAnimationFrame(chatKeyboardTrackFrame);
  }
}

function startChatKeyboardTracking(ms?: number): void {
  const dur = typeof ms === 'number' && ms > 0 ? ms : 520;
  keyboardTrackUntil = Math.max(keyboardTrackUntil, Date.now() + dur);
  if (keyboardTrackRaf != null) return;
  keyboardTrackRaf = requestAnimationFrame(chatKeyboardTrackFrame);
}

export function scheduleChatKeyboardInset(): void {
  startChatKeyboardTracking(520);
}

(function initChatKeyboardViewportListeners() {
  function sched(extraMs?: number): void {
    startChatKeyboardTracking(extraMs || 520);
  }

  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', function () {
      sched(600);
    }, { passive: true });
    window.visualViewport.addEventListener('scroll', function () {
      sched(600);
    }, { passive: true });
  }
  window.addEventListener('resize', function () {
    sched(400);
  }, { passive: true });
  window.addEventListener('orientationchange', function () {
    sched(900);
  });

  try {
    const t = getTg();
    if (t && typeof t.onEvent === 'function') {
      t.onEvent('viewportChanged', function (e: unknown) {
        const ev = e as { isStateStable?: boolean } | null;
        if (ev && ev.isStateStable === false) sched(900);
        else sched(450);
      });
    }
  } catch (_) {}

  document.addEventListener(
    'focusin',
    function (e: FocusEvent) {
      const el = e.target;
      if (!isElement(el)) return;
      if (el.matches('#owner-chat-input, #ai-chat-input')) sched(900);
    },
    true
  );
  document.addEventListener(
    'focusout',
    function (e: FocusEvent) {
      const el = e.target;
      if (!isElement(el)) return;
      if (el.matches('#owner-chat-input, #ai-chat-input')) sched(700);
    },
    true
  );

  if (typeof ResizeObserver !== 'undefined') {
    ['owner-chat-composer', 'ai-chat-composer'].forEach(function (id) {
      const node = document.getElementById(id);
      if (!node) return;
      try {
        new ResizeObserver(function () {
          updateChatComposerHeight();
        }).observe(node);
      } catch (_) {}
    });
  }
})();
